import { memo } from 'react';
import { AlertTriangle, Ban, ImageIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const InventoryCard = memo(({ item, type, onClick }) => {
  const isLowStock = item.quantity_available <= item.min_level && item.status !== 'Discontinued';
  const isDiscontinued = item.status === 'Discontinued';
  const outsideCount = item.quantity_total - item.quantity_available;
  const code = type === 'TL' ? item.tl_code : item.swatch_code;
  const unit = type === 'TL' ? 'm' : 'pcs';
  
  // Use photo_url directly from API response
  const photoUrl = item.photo_url ? `${BACKEND_URL}${item.photo_url}` : null;

  return (
    <div
      onClick={onClick}
      data-testid={`inventory-card-${code}`}
      className={`group relative overflow-hidden rounded-xl border shadow-sm transition-all hover:shadow-md cursor-pointer ${
        isDiscontinued
          ? 'border-slate-300 bg-slate-50 opacity-75'
          : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
    >
      {/* Fixed aspect ratio container for consistent card sizing */}
      <div className="aspect-square relative overflow-hidden bg-slate-100">
        {photoUrl ? (
          <div className="w-full h-full flex items-center justify-center bg-slate-50">
            <img
              src={photoUrl}
              alt={item.fabric_name}
              loading="lazy"
              className={`max-w-full max-h-full object-contain transition-transform duration-300 ${
                isDiscontinued ? 'grayscale opacity-60' : 'group-hover:scale-105'
              }`}
              style={{ 
                /* Preserve original aspect ratio, show full image */
                objectFit: 'contain',
              }}
            />
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-slate-300 text-4xl font-mono">{code.charAt(0)}</span>
          </div>
        )}
        
        {isDiscontinued && (
          <div
            className="absolute top-2 right-2 bg-slate-700 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1"
            data-testid="discontinued-badge"
          >
            <Ban className="w-3 h-3" />
            Discontinued
          </div>
        )}
        
        {isLowStock && !isDiscontinued && (
          <div
            className="absolute top-2 right-2 bg-orange-100 text-orange-700 border border-orange-200 px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1"
            data-testid="low-stock-badge"
          >
            <AlertTriangle className="w-3 h-3" />
            Low Stock
          </div>
        )}
      </div>

      <div className="p-4">
        <div
          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold mb-2 ${
            type === 'TL'
              ? 'border-cyan-200 bg-cyan-50 text-cyan-700'
              : 'border-indigo-200 bg-indigo-50 text-indigo-700'
          }`}
        >
          <span className="font-mono tracking-wider uppercase">{code}</span>
        </div>

        <h3 className={`text-base font-semibold mb-3 line-clamp-2 ${
          isDiscontinued ? 'text-slate-500' : 'text-slate-800'
        }`}>
          {item.fabric_name}
        </h3>

        <div className="space-y-2">
          <div className="flex items-baseline justify-between">
            <span className="text-xs text-slate-500 uppercase tracking-wider">Available</span>
            <span className={`text-2xl font-bold ${isDiscontinued ? 'text-slate-400' : 'text-slate-900'}`}>
              {item.quantity_available}
              <span className="text-sm font-mono text-slate-500 ml-1">{unit}</span>
            </span>
          </div>

          {outsideCount > 0 && (
            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              <span className="text-xs text-slate-400">Outside</span>
              <span className="text-sm font-mono text-slate-600">
                {outsideCount} {unit}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

InventoryCard.displayName = 'InventoryCard';

export default InventoryCard;
